import { Phone, Mail, MapPin, Clock, PhoneCall, ClipboardCheck, HeartHandshake } from "lucide-react";
import { SEO, buildLegalServiceSchema } from "@/components/seo/SEO";
import { ContactForm } from "@/components/shared/ContactForm";
import { Watermark } from "@/components/shared/Watermark";
import { MotionReveal, MotionStagger, MotionStaggerItem } from "@/components/shared/MotionReveal";
import { FIRM } from "@/constants/firmInfo";
import { useLanguage } from "@/i18n/LanguageContext";

const STEP_ICONS = [PhoneCall, ClipboardCheck, HeartHandshake];

export default function Contact() {
  const { t } = useLanguage();
  const mapQuery = encodeURIComponent(`${FIRM.addressLine1}, ${FIRM.city}, ${FIRM.state} ${FIRM.zip}`);
  const steps = t("contact.steps");

  return (
    <>
      <SEO
        title={t("seo.contact.title")}
        description={t("seo.contact.description")}
        path="/contact"
        jsonLd={buildLegalServiceSchema()}
      />

      <header className="relative bg-[color:var(--navy-950)] hero-noise overflow-hidden py-20 sm:py-24">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(1000px 500px at 20% 100%, rgba(184,154,61,0.18), transparent 60%)" }}
        />
        <Watermark word="Contact" position="center" className="text-white" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-medium tracking-[0.22em] uppercase text-[color:var(--gold-400)]">{t("contact.heroEyebrow")}</span>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-medium text-white text-balance">
            {t("contact.heroHeading1")} <span className="italic text-[color:var(--gold-400)]">{t("contact.heroHeadingEmphasis")}</span> {t("contact.heroHeadingEnd")}
          </h1>
          <p className="mt-4 text-slate-300 max-w-2xl mx-auto text-base sm:text-lg">
            {t("contact.heroSubtitle")}
          </p>
        </div>
      </header>

      <section className="relative py-16 sm:py-20 bg-[color:var(--paper)] overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(900px 420px at 20% 0%, rgba(184,154,61,0.08), transparent 60%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <MotionReveal className="lg:col-span-5 order-1">
            <span className="text-xs font-medium tracking-[0.22em] uppercase text-[color:var(--gold-500)]">{t("contact.whatNextEyebrow")}</span>
            <h2 className="mt-3 font-heading text-2xl text-[color:var(--navy-950)] mb-8">{t("contact.whatNextHeading")}</h2>

            <MotionStagger className="space-y-5 mb-10">
              {steps.map((step, idx) => {
                const StepIcon = STEP_ICONS[idx] || PhoneCall;
                return (
                  <MotionStaggerItem key={step.title}>
                    <div className="flex items-start gap-4">
                      <div className="h-11 w-11 rounded-2xl bg-[color:var(--navy-950)] grid place-items-center shrink-0">
                        <StepIcon className="h-5 w-5 text-[color:var(--gold-400)]" />
                      </div>
                      <div>
                        <h3 className="font-heading text-base text-[color:var(--navy-950)]">{idx + 1}. {step.title}</h3>
                        <p className="text-sm text-slate-600 mt-0.5 leading-relaxed">{step.body}</p>
                      </div>
                    </div>
                  </MotionStaggerItem>
                );
              })}
            </MotionStagger>

            <div className="rounded-3xl border border-slate-200 bg-[color:var(--sand)] p-6 sm:p-7 mb-6">
              <h3 className="font-heading text-lg text-[color:var(--navy-950)] mb-5">{t("contact.infoHeading")}</h3>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-[color:var(--gold-500)] mt-0.5 shrink-0" />
                  <span className="text-slate-700" data-testid="contact-address">
                    {FIRM.addressLine1}<br />{FIRM.city}, {FIRM.state} {FIRM.zip}
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-[color:var(--gold-500)] shrink-0" />
                  <a href={`tel:${FIRM.phoneTel}`} className="text-slate-700 hover:text-[color:var(--navy-950)] font-medium" data-testid="contact-phone">
                    {FIRM.phoneDisplay}
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-[color:var(--gold-500)] shrink-0" />
                  <a href={`mailto:${FIRM.email}`} className="text-slate-700 hover:text-[color:var(--navy-950)] font-medium" data-testid="contact-email">
                    {FIRM.email}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-[color:var(--gold-500)] mt-0.5 shrink-0" />
                  <span className="text-slate-700">{FIRM.hours}</span>
                </li>
              </ul>
            </div>

            <div className="rounded-3xl overflow-hidden border border-slate-200 h-56">
              <iframe
                title="Office location map"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                src={`https://www.google.com/maps?q=${mapQuery}&output=embed`}
                data-testid="contact-map-embed"
              />
            </div>
          </MotionReveal>

          <MotionReveal delay={0.1} className="lg:col-span-7 order-2">
            <div className="rounded-[2rem] border border-slate-200 bg-white p-6 sm:p-9 shadow-[0_30px_70px_rgba(11,18,32,0.10)]">
              <span className="text-xs font-medium tracking-[0.22em] uppercase text-[color:var(--gold-500)]">{t("contact.formEyebrow")}</span>
              <h2 className="mt-2 font-heading text-2xl text-[color:var(--navy-950)] mb-6">{t("contact.formHeading")}</h2>
              <ContactForm variant="full" source="contact_page" testIdPrefix="contact" />
            </div>
          </MotionReveal>
        </div>
      </section>
    </>
  );
}
